const DEFAULT_NO_DATA_MESSAGE =
  'No spend data found for that period. Try a different year or date range.';

const LAST_CONNECTOR_KEY = 'connections.lastImportConnectorId';
const NO_DATA_FLASH_KEY = 'connections.importNoDataFlash';

/**
 * Remember which connector started a sync so empty-job recovery can return there.
 * @param {string} connectorId
 */
export const rememberImportConnector = (connectorId) => {
  if (typeof sessionStorage === 'undefined' || !connectorId) return;
  try {
    sessionStorage.setItem(LAST_CONNECTOR_KEY, connectorId);
  } catch (_) {
    /* ignore quota / private mode */
  }
};

/**
 * @returns {string|null}
 */
export const readRememberedImportConnector = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(LAST_CONNECTOR_KEY);
  } catch (_) {
    return null;
  }
};

/**
 * Persist empty-import message so it survives connector page remount / descriptor reload.
 * @param {string|null} connectorId
 * @param {string} message
 */
export const writeImportNoDataFlash = (connectorId, message) => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      NO_DATA_FLASH_KEY,
      JSON.stringify({
        connectorId: connectorId || null,
        message: message || DEFAULT_NO_DATA_MESSAGE,
        at: Date.now(),
      }),
    );
  } catch (_) {
    /* ignore */
  }
};

/**
 * Read and clear a pending empty-import flash for this connector (or hub).
 * @param {string} [connectorId]
 * @returns {string|null}
 */
export const consumeImportNoDataFlash = (connectorId) => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(NO_DATA_FLASH_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (connectorId && data.connectorId && data.connectorId !== connectorId) return null;
    sessionStorage.removeItem(NO_DATA_FLASH_KEY);
    return data.message || DEFAULT_NO_DATA_MESSAGE;
  } catch (_) {
    return null;
  }
};

/**
 * Sync / job payloads that mean "empty window" rather than a hard failure.
 * @param {object} [payload]
 * @returns {boolean}
 */
export const isImportNoData = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.no_data === true || payload.noData === true) return true;
  if (payload.data?.no_data === true || payload.data?.noData === true) return true;

  const status = payload.pipelineStatus || payload.status || payload.pipeline_status;
  if (status === 'COMPLETE' && (payload.no_data || payload.noData)) return true;

  const stage = String(payload.failureStage || payload.failure_stage || '').toLowerCase();
  const err = String(payload.lastError || payload.last_error || payload.error || payload.message || '').toLowerCase();
  const failed = status === 'FAILED';
  if (!failed) return false;

  if (stage === 'connector_staging' || stage.includes('connector_staging')) return true;
  if (err.includes('no staging rows') || err.includes('no spend data') || err.includes('no data found')) return true;
  return false;
};

/**
 * @param {object} [payload]
 * @returns {string}
 */
export const importNoDataMessage = (payload) =>
  payload?.message || payload?.data?.message || DEFAULT_NO_DATA_MESSAGE;

/**
 * Resolve connector id from job + navigation/session hints.
 * @param {object} [job]
 * @param {object} [locationState]
 * @returns {string|null}
 */
export const resolveImportConnectorId = (job, locationState) =>
  job?.connectorId ||
  job?.connector_id ||
  job?.sourceConnectorId ||
  job?.metadata?.connectorId ||
  locationState?.fromConnectorId ||
  readRememberedImportConnector() ||
  null;

export { DEFAULT_NO_DATA_MESSAGE };
